import {
  Button,
  Card,
  Col,
  Drawer,
  InputNumber,
  message,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
} from "antd";
import moment from "moment";
import { useEffect, useState } from "react";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import {
  createStaffAssignment,
  getStaffAssignments,
  softDeleteStaffProjectAssignments,
  updateStaffAssignment,
} from "../../../core/store/reducers/project.slice";
import { API_GET_ALL_STAFF_LIST } from "../../../shared/routes/private.routes";
import { Client } from "../../../core/api-client";
import {
  accessAllowed,
  showNotification,
  decryptCTC,
  formatCost1,
} from "../../../shared/utils/utils";
import {
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import { RootState } from "../../../shared/types/common";
import { allPermissions } from "../../../shared/constants/constants";

interface Props {
  onClose: () => void;
  visible: boolean;
  selectedEntity?: any;
  onSubmit?: () => void;
}

const StaffAssignmentComponent: React.FC<Props> = ({
  onClose,
  visible,
  selectedEntity = null,
  onSubmit = () => {
    return;
  },
}: Props) => {
  const dispatch = useDispatch();
  const [staffData, setStaffData] = useState<any[]>([]);
  const [monthCols, setMonthCols] = useState<any[]>([]);
  const [dataSource, setDataSource] = useState<any[]>([]);
  const [staffList, setStaffList] = useState<any[]>([]);
  const [showAddResource, setShowAddResource] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<any>(null);
  const [newAllocation, setNewAllocation] = useState<any | null>(null);
  const [loadingList, setLoadingList] = useState(false);
  const [editingRowId, setEditingRowId] = useState<string | null>(null); // track which row is in edit mode
  const [isValid, setIsValid] = useState(false);
  const [totalCost, setTotalCost] = useState<number>(0);
  const { myPermissions } = useSelector((state: RootState) => state.auth);
  const assignmentTitle =
    selectedEntity?.l2ProjectName || selectedEntity?.projectName || "Project";

  const getMonthMaxAllocation = (staffId: string, month: string) => {
    const staff = staffList.find((item) => item?.staffId === staffId);
    const allocations = Array.isArray(staff?.monthlyAllocationProjects)
      ? staff.monthlyAllocationProjects
      : null;
    const lopAllocations = Array.isArray(staff?.monthlyLopAllocations)
      ? staff.monthlyLopAllocations
      : [];

    if (!allocations) {
      const lopPercent = lopAllocations
        .filter((allocation: any) => allocation?.month === month)
        .reduce(
          (sum: number, allocation: any) =>
            sum + (Number(allocation?.lopPercent) || 0),
          0,
        );
      return Math.max(100 - lopPercent, 0);
    }

    const otherProjectAllocation = allocations.reduce(
      (sum: number, allocation: any) => {
        if (
          allocation?.projectId === selectedEntity?.projectId ||
          allocation?.month !== month
        ) {
          return sum;
        }
        return sum + (Number(allocation?.percentAllocation) || 0);
      },
      0,
    );
    const lopPercent = lopAllocations
      .filter((allocation: any) => allocation?.month === month)
      .reduce(
        (sum: number, allocation: any) =>
          sum + (Number(allocation?.lopPercent) || 0),
        0,
      );

    return Math.max(100 - otherProjectAllocation - lopPercent, 0);
  };

  const showMonthCapacityError = (maxAllocation: number, month: string) => {
    const monthLabel = dayjs(month, "YYYY-MM").format("MMM 'YY");
    if (maxAllocation <= 0) {
      message.error(
        `This resource is already fully allocated for ${monthLabel}`,
      );
      return;
    }
    message.error(
      `Only ${maxAllocation}% allocation is available for ${monthLabel} after existing allocations and LOP`,
    );
  };

  const calculateTotalCost = async (dataSource: any[], staffData: any[]) => {
    if (!staffData.length) {
      setTotalCost(0);
      return;
    }

    let projectTotal = 0;

    // Iterate through each staff
    for (const staff of staffData) {
      if (!staff.allocations || !staff.allocations.length) continue;

      // Iterate through each allocation for this staff
      for (const allocation of staff.allocations) {
        const percentAllocation = Number(allocation.percentAllocation) || 0;

        // Skip if no allocation
        if (percentAllocation <= 0) continue;

        let staffCtc = allocation?.staffSalary?.ctc || 0;

        // Decrypt CTC if encrypted
        if (allocation?.staffSalary?.encrypted_ctc) {
          try {
            staffCtc = await decryptCTC(
              moment(
                allocation?.staffSalary?.updated_date || new Date(),
              ).format("YYYY-MM-DD"),
              allocation?.staffSalary?.encrypted_ctc,
              allocation?.staffSalary?.iv,
              allocation?.staffSalary?.salt,
            );
          } catch (err) {
            console.error("decryptCTC failed:", err, allocation);
            staffCtc = 0;
          }
        }

        // Calculate cost for this allocation
        const staffCost =
          (Number(staffCtc || 0) / 12) *
          Number(allocation?.inrMultiplier || 1) *
          (percentAllocation / 100);

        const totalSpendInProjectCurrency =
          staffCost /
          Number(
            allocation?.projectCurrencyRate ||
              selectedEntity?.projectCurrencyRate ||
              1,
          );

        projectTotal += totalSpendInProjectCurrency;
      }
    }

    setTotalCost(projectTotal);
  };

  useEffect(() => {
    if (visible && selectedEntity) {
      fetchStaffList();
      fetchStaffAssignments();
      const monthColumns = getMonthColumns();
      setMonthCols(monthColumns);
    }
  }, [visible, selectedEntity]);

  useEffect(() => {
    if (staffData.length > 0 && monthCols.length > 0) {
      const dataSource = mapAllocationsToRows(staffData, monthCols);
      setDataSource(dataSource);
      calculateTotalCost(dataSource, staffData);
    } else {
      setDataSource([]);
      setTotalCost(0);
    }
  }, [staffData, monthCols]);

  // Recalculate cost when dataSource changes (during editing)
  useEffect(() => {
    if (dataSource.length > 0 && staffData.length > 0) {
      calculateTotalCost(dataSource, staffData);
    }
  }, [dataSource]);

  const fetchStaffList = async () => {
    setLoadingList(true);
    const response = await Client.get(API_GET_ALL_STAFF_LIST);
    if (response?.data?.data) {
      setStaffList(response?.data?.data);
    } else {
      setStaffList([]);
    }
    setLoadingList(false);
  };

  const fetchStaffAssignments = async () => {
    if (selectedEntity?.projectId) {
      const response = await dispatch(
        getStaffAssignments(selectedEntity?.projectId),
      ).unwrap();
      if (response?.data?.data) {
        setStaffData(response?.data?.data);
      }
    }
  };

  const activeStaffList = staffList
    .filter((staff) => staff?.staffStatusId !== "INACTIVE")
    .sort((a, b) =>
      (a?.staffName || "").localeCompare(b?.staffName || ""),
    );
  const staffDropdownOptions = activeStaffList.map((staff) => ({
    value: staff?.staffId,
    label: `${staff?.staffName} (${staff?.employeeId})`,
  }));

  const mapAllocationsToRows = (
    data: any[],
    monthCols: { dataIndex: string }[],
  ) => {
    return [...data]
      .sort((a, b) =>
        (a?.staffName || "").localeCompare(b?.staffName || ""),
      )
      .map((staff) => {
        const row: any = {
          staffId: staff.staffId,
          staffName: `${staff.staffName} (${staff.employeeId})`,
          projectId: staff.projectId,
        };

        monthCols.forEach((col: { dataIndex: string }) => {
          const alloc = staff.allocations.find((a: any) => {
            const allocKey = dayjs(a.month, "YYYY-MM", true)
              .format("MMMYY")
              .toLowerCase();
            return allocKey === col.dataIndex;
          });

          row[col.dataIndex] = alloc ? alloc.percentAllocation : 0;
          row[col.dataIndex + "_salary"] = alloc?.staffSalary?.iv ? 1 : 0;
        });

        return row;
      });
  };

  // --- Month column generator ---
  const getMonthColumns = (
    isEditable?: boolean,
    onValueChange?: (staffId: string, monthKey: string, value: number) => void,
  ) => {
    const start = dayjs(selectedEntity.startDate);
    const end = dayjs(selectedEntity.endDate);
    const cols: any[] = [];
    let current = start.startOf("month");

    while (current.isBefore(end) || current.isSame(end, "month")) {
      const colKey = current.format("MMMYY").toLowerCase();
      const ym = current.format("YYYY-MM");

      cols.push({
        title: current.format("MMM 'YY"),
        dataIndex: colKey,
        key: colKey,
        ym,
        render: (value: any, record: any) =>
          isEditable ? (
            <InputNumber
              style={{ width: 100 }}
              min={0}
              // max={100}
              addonAfter="%"
              value={record[colKey] ?? 0}
              onChange={(val) => {
                const num = Number(val) || 0;
                const maxAllocation = getMonthMaxAllocation(record.staffId, ym);
                if (num > 100) {
                  message.error("Allocation cannot exceed 100%");
                  setIsValid(true);
                  return;
                }
                if (num > maxAllocation) {
                  showMonthCapacityError(maxAllocation, ym);
                  setIsValid(true);
                  return;
                }
                setIsValid(false);
                onValueChange?.(record.staffId, colKey, num);
              }}
              onKeyDown={(e: any) => {
                if (
                  !/[0-9]/.test(e.key) &&
                  e.key !== "Backspace" &&
                  e.key !== "Delete" &&
                  e.key !== "ArrowLeft" &&
                  e.key !== "ArrowRight" &&
                  e.key !== "Tab"
                ) {
                  e.preventDefault();
                }
              }}
            />
          ) : (
            (value ?? 0)
          ),
      });

      current = current.add(1, "month");
    }

    return cols;
  };

  // --- Update existing allocation ---
  const handleExistingAllocationChange = (
    staffId: string,
    monthKey: string,
    value: number,
  ) => {
    setDataSource((prev) => {
      const updated = prev.map((row) =>
        row.staffId === staffId ? { ...row, [monthKey]: value ?? 0 } : row,
      );
      // Update staffData allocations to reflect the change
      const updatedStaffData = staffData.map((staff) => {
        if (staff.staffId === staffId) {
          let hasMonthAllocation = false;
          const updatedAllocations = staff.allocations.map((alloc: any) => {
            const allocKey = dayjs(alloc.month, "YYYY-MM", true)
              .format("MMMYY")
              .toLowerCase();
            if (allocKey === monthKey) {
              hasMonthAllocation = true;
              return { ...alloc, percentAllocation: value ?? 0 };
            }
            return alloc;
          });
          if (!hasMonthAllocation) {
            const month = monthCols.find(
              (col) => col.dataIndex === monthKey,
            )?.ym;
            if (month) {
              updatedAllocations.push({
                month,
                percentAllocation: value ?? 0,
              });
            }
          }
          return { ...staff, allocations: updatedAllocations };
        }
        return staff;
      });
      setStaffData(updatedStaffData);
      // Recalculate total cost after allocation change
      if (updatedStaffData.length > 0) {
        calculateTotalCost(updated, updatedStaffData);
      }
      return updated;
    });
  };

  const handleUpdate = async (row: any) => {
    const allocationArray = monthCols.map((col) => ({
      month: col.ym,
      percentAllocation: Number(row[col.dataIndex]) || 0,
    }));

    const payload = {
      staffId: row.staffId,
      projectId: row.projectId,
      allocation: allocationArray,
    };

    const res = await dispatch(updateStaffAssignment(payload));
    if (res?.payload?.status === 400) {
      showNotification(
        res?.payload?.data?.message || "Failed to update staff allocation",
        "error",
      );
      return;
    }
    if (res?.payload?.data) {
      showNotification("Staff Allocation Updated", "success");
      fetchStaffAssignments();
      fetchStaffList();
      onSubmit();
      setEditingRowId(null);
      setNewAllocation(null);
      setIsValid(false);
    }
  };

  const handleSoftDelete = async (record: any) => {
    try {
      await dispatch(
        softDeleteStaffProjectAssignments({
          staffId: record.staffId,
          projectId: record.projectId,
        }),
      ).unwrap();
      showNotification("Resource removed from project", "success");
      setEditingRowId(null);
      await fetchStaffAssignments();
      onSubmit();
    } catch {
      showNotification("Failed to remove resource", "error");
    }
  };

  // --- Handle select staff for new resource ---
  const handleChange = (staffId: string) => {
    setIsValid(false);
    const staffName =
      staffDropdownOptions.find((s) => s.value === staffId)?.label || "";

    const row: any = {
      staffId,
      staffName,
      projectId: selectedEntity.projectId,
    };
    getMonthColumns(true).forEach((col) => {
      row[col.dataIndex] = 0;
    });

    setNewAllocation(row);
  };

  // --- Update allocation input for new resource ---
  const handleAllocationChange = (monthKey: string, value: number) => {
    setNewAllocation((prev: any) => ({
      ...prev,
      [monthKey]: value ?? 0,
    }));
  };

  // --- Table for existing allocations ---
  const editableColumns = [
    {
      title: "Resource Name",
      key: "staffName",
      dataIndex: "staffName",
      width:'250px'
    },
    ...monthCols.map((col) => ({
      ...col,
      render: (value: any, record: any) =>
        editingRowId === record.staffId ? (
          <InputNumber
            style={{ width: 100 }}
            min={0}
            // max={100}
            addonAfter="%"
            value={record[col.dataIndex] ?? 0}
            onChange={(val) => {
              const num = Number(val) || 0;
              const maxAllocation = getMonthMaxAllocation(
                record.staffId,
                col.ym,
              );
              if (num > 100) {
                message.error("Allocation cannot exceed 100%");
                setIsValid(true);
                return;
              }
              if (num > maxAllocation) {
                showMonthCapacityError(maxAllocation, col.ym);
                setIsValid(true);
                return;
              }
              setIsValid(false);
              handleExistingAllocationChange(
                record.staffId,
                col.dataIndex,
                val as number,
              );
            }}
          />
        ) : (
          // ✅ append % only in view mod
          <div
            style={
              record[col.dataIndex + "_salary"] === 1 ? {} : {
                color: 'red',
                border: '1px solid red',
                width: '40px', textAlign: 'center'
              }
            }
          >
            {value !== undefined && value !== null ? `${value}%` : "0%"}
          </div>
        ),
    })),
    accessAllowed(
      myPermissions,
      allPermissions.PROJECT_UPDATE_STAFF_ASSIGNMENT,
    ) && {
      title: "Action",
      key: "action",
      render: (_: any, record: any) =>
        editingRowId === record.staffId ? (
          <Space>
            <Button
              type="link"
              icon={<SaveOutlined />}
              onClick={() => handleUpdate(record)}
              disabled={isValid}
            />
            <Button
              type="link"
              icon={<CloseOutlined />}
              onClick={() => {
                setIsValid(false);
                setEditingRowId(null);
              }}
            />
          </Space>
        ) : (
          <Space>
            <Button
              type="link"
              icon={<EditOutlined />}
              onClick={() => {
                setIsValid(false);
                setEditingRowId(record.staffId);
              }}
            />
            <Popconfirm
              title="Remove this resource from the project? All allocation rows for this staff will be marked inactive and hidden."
              onConfirm={() => handleSoftDelete(record)}
              okText="Yes"
              cancelText="No"
            >
              <Button type="link" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        ),
    },
  ].filter(Boolean);

  const ResourceAllocationTable = () => (
    <Table
      dataSource={dataSource}
      columns={editableColumns}
      loading={loadingList}
      pagination={false}
      bordered
      rowKey="staffId"
      scroll={{ x: "max-content", y: 400 }}
    />
  );

  // --- Table for new allocation (always editable) ---
  const createColumns = [
    ...getMonthColumns(true, (_staffId, monthKey, value) =>
      handleAllocationChange(monthKey, value),
    ),
  ];
  const AllocationTable = () => (
    <Table
      dataSource={newAllocation ? [newAllocation] : []}
      columns={createColumns}
      pagination={false}
      bordered
      rowKey="staffId"
      scroll={{ x: "max-content" }}
    />
  );

  const handleCancel = () => {
    setIsValid(false);
    setShowAddResource(false);
    setNewAllocation(null);
  };

  const handleSave = () => {
    if (!newAllocation) return;

    const allocationArray = monthCols.map((col) => ({
      month: col.ym,
      percentAllocation: Number(newAllocation[col.dataIndex]) || 0,
    }));

    const payload = {
      staffId: newAllocation.staffId,
      projectId: newAllocation.projectId,
      allocation: allocationArray,
    };

    dispatch(createStaffAssignment(payload)).then((val: any) => {
      if (val?.payload?.status === 400) {
        showNotification(
          val?.payload?.data?.message || "Failed to assign staff",
          "error",
        );
        return;
      }
      if (val?.payload?.data) {
        showNotification("Staff Assigned Successfully", "success");
        setShowAddResource(false);
        setNewAllocation(null);
        fetchStaffAssignments();
        onSubmit();
        setIsValid(false);
      }
    });
  };

  const onDrawerClose = () => {
    setStaffData([]);
    setDataSource([]);
    setTotalCost(0);
    setEditingRowId(null);
    setNewAllocation(null);
    setShowAddResource(false);
    setIsValid(false);
    onClose();
  };

  return (
    <Drawer
      open={visible}
      onClose={onDrawerClose}
      title={`Resource Assignment - ${assignmentTitle}`}
      width={"90%"}
    >
      <Row>
        <Col span={12}>
          SOW Start Date :{" "}
          {selectedEntity?.startDate
            ? moment(selectedEntity.startDate).format("DD-MMM-YYYY")
            : ""}
        </Col>
        <Col span={12}>
          SOW End Date :{" "}
          {selectedEntity?.endDate
            ? moment(selectedEntity.endDate).format("DD-MMM-YYYY")
            : ""}
        </Col>
      </Row>
      <Row className="mt-4">
        <Col span={12}>
          PO Value :{" "}
          {formatCost1(
            selectedEntity?.poValue || 0,
            selectedEntity?.poCurrencyName || "RM",
          )}
        </Col>
        <Col span={6}>
          Total Cost :{" "}
          {formatCost1(totalCost, selectedEntity?.poCurrencyName || "RM")}
        </Col>
        <Col span={6}>
          <div style={{
            color: 'red',
            border: '1px solid red',
            textAlign: 'center'
          }}>CTC is not specicied for this month</div> 
        </Col>
      </Row>

      <div className="mt-4 mb-3">{ResourceAllocationTable()}</div>

      {!showAddResource && (
        <Row justify={"end"}>
          {accessAllowed(
            myPermissions,
            allPermissions.PROJECT_ADD_STAFF_ASSIGNMENT,
          ) && (
            <Button
              className="mt-3"
              type="primary"
              onClick={() => setShowAddResource(true)}
            >
              Add New Resource
            </Button>
          )}
        </Row>
      )}
      {showAddResource && (
        <Card className="mt-10 bg-primary-gray">
          <strong>Add New Resource</strong>
          <Row className="mt-3">
            <span style={{ marginRight: 10 }}>Select Resource:</span>
            <Select
              placeholder="Select Resource"
              style={{ width: 250 }}
              onChange={handleChange}
              options={activeStaffList.map((staff) => ({
                value: staff.staffId,
                label: `${staff.staffName} (${staff.employeeId})`,
                disabled: staffData.some((s) => s.staffId === staff.staffId),
              }))}
              showSearch={true}
              filterOption={(input, option: any) =>
                option?.label?.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            />
          </Row>

          {newAllocation && <div className="mt-4">{AllocationTable()}</div>}

          <Row justify="end" style={{ marginTop: 20 }}>
            <Button style={{ marginRight: 10 }} onClick={() => handleCancel()}>
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={() => handleSave()}
              disabled={isValid || !newAllocation}
            >
              Save
            </Button>
          </Row>
        </Card>
      )}
    </Drawer>
  );
};

export default StaffAssignmentComponent;
